
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, ChevronDown, Search, ShieldCheck, ArrowLeft, User, Clock, CheckCircle, Copy, Zap, Info } from 'lucide-react';
import { useWallet } from '../../../hooks/useWallet';
import { Token } from '../../../types';
import { Button } from '../../ui/Button';
import { TokenIcon, ChainIcon } from '../../ui/ChainIcons';

interface SendModalProps {
    onCancel: () => void;
    onComplete: () => void;
    balances: Token[];
}

export const SendModal: React.FC<SendModalProps> = ({ onCancel, onComplete, balances }) => {
    const { sendTransaction, simulateTransaction } = useWallet();

    const [step, setStep] = useState<'details' | 'review'>('details');
    const [isProcessing, setIsProcessing] = useState(false);

    const [recipient, setRecipient] = useState("");
    const [amount, setAmount] = useState("");
    const [search, setSearch] = useState("");
    const [selectedToken, setSelectedToken] = useState<Token | null>(balances[0] || null);
    const [stealthMode, setStealthMode] = useState(false);

    const [gasEstimate, setGasEstimate] = useState<string>("—");
    const [simResult, setSimResult] = useState<string | null>(null);
    
    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    
    // Institutional Contacts
    const recentContacts = [
        { name: "Treasury A", address: "8x92...3k1" },
        { name: "Ops Multi-sig", address: "4k21...9x2" },
        { name: "Verified Node", address: "9l10...2m4" }
    ];

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredTokens = useMemo(() => balances.filter(t => t.symbol.toLowerCase().includes(search.toLowerCase()) || t.name.toLowerCase().includes(search.toLowerCase())), [search, balances]);

    const handleSelectToken = (token: Token) => {
        setSelectedToken(token);
        setDropdownOpen(false);
        setSearch('');
    };

    const handleMaxAmount = () => {
        if (selectedToken) {
            setAmount(selectedToken.balance.toString());
        }
    };

    const handleReview = async () => {
        if (!selectedToken || !amount || !recipient) return;
        setIsProcessing(true);
        try {
            const estimate = await simulateTransaction({ token: selectedToken, amount: parseFloat(amount), to: recipient, stealth: stealthMode });
            setGasEstimate(estimate.fee);
            setSimResult(estimate.summary);
            setStep('review');
        } catch (error) {
            console.error("Simulation failed:", error);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleSend = async () => {
        if (!selectedToken || !amount || !recipient) return;
        setIsProcessing(true);
        try {
            await sendTransaction({ token: selectedToken, to: recipient, amount: parseFloat(amount), stealth: stealthMode });
            onComplete();
        } catch(error) {
            console.error("Send failed:", error);
        } finally {
            setIsProcessing(false);
        }
    };

    const isDetailsValid = recipient && amount && selectedToken && parseFloat(amount) > 0;

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
            <motion.div initial={{ scale: 0.98, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.98, y: 10 }} className="bg-white dark:bg-[#15171C] rounded-sm shadow-2xl w-full max-w-md relative border border-stone-200 dark:border-[#2D3142] overflow-hidden">
                <div className="p-5 border-b border-stone-100 dark:border-white/5 flex justify-between items-center bg-gray-50 dark:bg-[#0A0B0F]">
                    <div className="flex items-center gap-3">
                        {step === 'review' ? (
                            <button onClick={() => setStep('details')} className="p-1 hover:bg-stone-200 dark:hover:bg-white/10 rounded-sm text-stone-500 transition-colors">
                                <ArrowLeft size={18} />
                            </button>
                        ) : (
                            <Zap size={18} className="text-nobel-gold"/>
                        )}
                        <h2 className="font-serif text-lg text-[#0A0B0F] dark:text-white font-bold uppercase tracking-widest">
                            {step === 'details' ? 'Initiate Transfer' : 'Confirm Sequence'}
                        </h2>
                    </div>
                    <button onClick={onCancel} className="text-stone-400 hover:text-stone-900 dark:hover:text-white transition-colors">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    <AnimatePresence mode="wait">
                        {step === 'details' && (
                            <motion.div key="details" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }} className="space-y-5">
                                {/* Section: Recipient */}
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-stone-400 uppercase tracking-[0.25em] ml-1">Destination Address</label>
                                    <div className="relative group">
                                        <input 
                                            type="text" 
                                            value={recipient} 
                                            onChange={e => setRecipient(e.target.value)} 
                                            className="w-full p-4 pl-12 bg-stone-50 dark:bg-black/40 text-sm font-bold text-[#0A0B0F] dark:text-white rounded-sm border border-stone-200 dark:border-white/10 focus:border-nobel-gold outline-none transition-all shadow-inner" 
                                            placeholder="GHOST, SOL, or SUI Address" 
                                        />
                                        <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 group-focus-within:text-nobel-gold transition-colors" />
                                    </div>
                                    
                                    {!recipient && (
                                        <div className="flex gap-1.5 overflow-x-auto no-scrollbar py-1">
                                            {recentContacts.map((contact, i) => (
                                                <button key={i} onClick={() => setRecipient(contact.address)} className="flex items-center gap-2 bg-stone-100 dark:bg-white/5 border border-stone-200 dark:border-white/5 rounded-sm px-3 py-1.5 hover:border-stone-400 transition-all shrink-0">
                                                    <div className="w-1.5 h-1.5 rounded-full bg-nobel-gold"></div>
                                                    <span className="text-[9px] font-black text-stone-500 uppercase tracking-widest">{contact.name}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Section: Asset Selection */}
                                <div className="space-y-2" ref={dropdownRef}>
                                    <label className="text-[10px] font-black text-stone-400 uppercase tracking-[0.25em] ml-1">Protocol Asset</label>
                                    <div className="relative">
                                        <button 
                                            onClick={() => setDropdownOpen(!isDropdownOpen)} 
                                            className={`w-full p-4 bg-stone-50 dark:bg-black/40 text-[#0A0B0F] dark:text-white rounded-sm border transition-all flex justify-between items-center text-left ${isDropdownOpen ? 'border-nobel-gold ring-1 ring-nobel-gold/10' : 'border-stone-200 dark:border-white/10'}`}
                                        >
                                           {selectedToken ? (
                                                <div className="flex items-center gap-3">
                                                    <div className="relative">
                                                        <TokenIcon symbol={selectedToken.symbol} fallback={selectedToken.icon} className="w-8 h-8 rounded-sm shadow-sm" />
                                                        <div className="absolute -bottom-1 -right-1">
                                                            <ChainIcon chain={selectedToken.chain} className="w-4 h-4 border border-white dark:border-[#15171C] rounded-full" />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="font-black text-xs uppercase tracking-wider">{selectedToken.symbol}</div>
                                                        <div className="text-[9px] text-stone-500 font-bold uppercase tracking-widest">{selectedToken.name}</div>
                                                    </div>
                                                </div>
                                           ) : (
                                                <span className="text-stone-400 text-xs font-bold uppercase tracking-widest">Select Network Asset</span>
                                           )}
                                            <ChevronDown size={16} className={`transition-transform text-stone-400 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                                        </button>
                                        
                                        <AnimatePresence>
                                        {isDropdownOpen && (
                                            <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 5 }} className="absolute z-20 bottom-full mb-2 w-full bg-white dark:bg-[#1A1D23] border border-stone-200 dark:border-[#2D3142] rounded-sm shadow-2xl overflow-hidden">
                                                <div className="p-3 border-b border-stone-100 dark:border-white/5 bg-stone-50 dark:bg-black/20">
                                                    <div className="relative">
                                                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                                                        <input type="text" placeholder="Filter assets..." value={search} onChange={e => setSearch(e.target.value)} className="w-full bg-white dark:bg-white/5 text-[#0A0B0F] dark:text-white p-2.5 pl-9 text-[10px] font-bold uppercase tracking-widest rounded-sm border border-stone-200 dark:border-white/5 outline-none focus:border-nobel-gold" />
                                                    </div>
                                                </div>
                                                <div className="max-h-56 overflow-y-auto no-scrollbar divide-y divide-stone-50 dark:divide-white/5">
                                                    {filteredTokens.map(t => (
                                                        <button key={t.mint} onClick={() => handleSelectToken(t)} className="w-full flex items-center justify-between gap-3 p-4 hover:bg-stone-50 dark:hover:bg-white/5 transition-colors text-left group">
                                                            <div className="flex items-center gap-4">
                                                                <TokenIcon symbol={t.symbol} fallback={t.icon} className="w-9 h-9 rounded-sm shadow-sm group-hover:scale-105 transition-transform" />
                                                                <div>
                                                                    <div className="font-black text-xs uppercase tracking-wider text-[#0A0B0F] dark:text-white">{t.symbol}</div>
                                                                    <div className="text-[9px] text-stone-500 font-bold uppercase tracking-widest">{t.name}</div>
                                                                </div>
                                                            </div>
                                                            <div className="text-right">
                                                                <div className="font-bold text-xs text-[#0A0B0F] dark:text-white">{t.balance.toLocaleString()}</div>
                                                                <div className="text-[9px] text-stone-400 font-mono font-bold tracking-tighter">${(t.balance * t.priceUsd).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                                            </div>
                                                        </button>
                                                    ))}
                                                </div>
                                            </motion.div>
                                        )}
                                        </AnimatePresence>
                                    </div>
                                </div>

                                {/* Section: Amount */}
                                <div className="space-y-2">
                                    <div className="flex justify-between items-center px-1">
                                        <label className="text-[10px] font-black text-stone-400 uppercase tracking-[0.25em]">Transfer Quantity</label>
                                        {selectedToken && <span className="text-[9px] font-mono font-bold text-stone-400 uppercase">Avail: {selectedToken.balance.toLocaleString()}</span>}
                                    </div>
                                    <div className="relative group">
                                        <input 
                                            type="number" 
                                            value={amount} 
                                            onChange={e => setAmount(e.target.value)} 
                                            className="w-full p-5 bg-stone-50 dark:bg-black/40 text-[#0A0B0F] dark:text-white rounded-sm border border-stone-200 dark:border-white/10 focus:border-nobel-gold outline-none pr-20 text-3xl font-mono font-bold shadow-inner" 
                                            placeholder="0.00" 
                                        />
                                        <button 
                                            onClick={handleMaxAmount}
                                            disabled={!selectedToken}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-[9px] font-black bg-stone-200 dark:bg-white/10 text-stone-600 dark:text-stone-300 px-3 py-1.5 rounded-sm hover:bg-stone-300 dark:hover:bg-white/20 transition-all uppercase tracking-widest disabled:opacity-30"
                                        >
                                            MAX
                                        </button>
                                    </div>
                                    {selectedToken && (
                                        <div className="flex justify-between items-center px-1">
                                            <div className="text-[10px] font-bold text-stone-500 uppercase tracking-widest">
                                                Valuation: <span className="text-nobel-gold font-mono font-black tracking-tight">${(parseFloat(amount || '0') * selectedToken.priceUsd).toLocaleString(undefined, {minimumFractionDigits: 2})} USD</span>
                                            </div>
                                            {amount && parseFloat(amount) > selectedToken.balance && (
                                                <div className="text-[9px] font-black text-red-500 uppercase tracking-widest flex items-center gap-1">
                                                    <Info size={10}/> Overdraw
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Section: Policy Enforcement */}
                                <div className="p-4 bg-stone-50 dark:bg-white/[0.02] border border-stone-100 dark:border-white/5 rounded-sm space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className={`p-2 rounded-sm transition-colors ${stealthMode ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20' : 'bg-stone-100 dark:bg-white/5 text-stone-400'}`}>
                                                <ShieldCheck size={18} />
                                            </div>
                                            <div>
                                                <span className="text-[11px] font-bold text-[#0A0B0F] dark:text-stone-300 uppercase tracking-wider">Stealth Protocol</span>
                                                <p className="text-[9px] text-stone-500 font-bold uppercase tracking-widest mt-0.5">One-time isolated address</p>
                                            </div>
                                        </div>
                                        <button 
                                            onClick={() => setStealthMode(!stealthMode)}
                                            className={`w-11 h-6 rounded-full p-1 transition-all ${stealthMode ? 'bg-emerald-500 shadow-inner' : 'bg-stone-200 dark:bg-stone-800'}`}
                                        >
                                            <div className={`w-4 h-4 rounded-full bg-white shadow-md transition-transform ${stealthMode ? 'translate-x-5' : 'translate-x-0'}`}></div>
                                        </button>
                                    </div>
                                </div>

                                <div className="pt-4">
                                    <Button onClick={handleReview} variant="secondary" isLoading={isProcessing} disabled={!isDetailsValid || parseFloat(amount) > (selectedToken?.balance || 0)} className="w-full h-14 rounded-sm text-xs font-black uppercase tracking-[0.2em] shadow-xl">
                                        Preview Transaction
                                    </Button>
                                </div>
                            </motion.div>
                        )}

                        {step === 'review' && (
                            <motion.div key="review" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} className="space-y-6">
                                <div className="p-8 bg-stone-900 text-white rounded-sm border border-white/10 text-center relative overflow-hidden group shadow-2xl">
                                    <div className="absolute top-0 right-0 p-4 opacity-[0.02] pointer-events-none group-hover:opacity-[0.05] transition-opacity">
                                        <Zap size={140} className="rotate-12" />
                                    </div>
                                    <div className="relative z-10 space-y-2">
                                        <div className="text-[10px] text-stone-500 font-black uppercase tracking-[0.3em] mb-4">Confirming Inbound Flow</div>
                                        <div className="text-5xl font-mono font-bold text-white tracking-tighter leading-none">{parseFloat(amount).toLocaleString()}</div>
                                        <div className="text-xl font-serif text-nobel-gold font-bold italic">{selectedToken?.symbol}</div>
                                        <div className="pt-6 border-t border-white/5 mt-6 flex justify-between items-end">
                                            <div className="text-left">
                                                <div className="text-[8px] text-stone-600 font-bold uppercase tracking-widest">USD Value</div>
                                                <div className="text-lg font-mono font-bold text-white/80 tracking-tight">
                                                    ≈ ${(parseFloat(amount) * (selectedToken?.priceUsd ?? 0)).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-[8px] text-stone-600 font-bold uppercase tracking-widest">Network</div>
                                                <div className="flex items-center gap-2 justify-end mt-1">
                                                    <ChainIcon chain={selectedToken?.chain || 'SOL'} className="w-4 h-4" />
                                                    <span className="text-[10px] font-black uppercase tracking-widest">{selectedToken?.chain} Protocol</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-stone-400 uppercase tracking-[0.25em] ml-1">Execution Metrics</label>
                                    <div className="divide-y divide-stone-100 dark:divide-white/5 border border-stone-200 dark:border-white/10 rounded-sm overflow-hidden bg-white dark:bg-black/20">
                                        <div className="flex justify-between p-4 items-center">
                                            <span className="text-[11px] font-bold text-stone-500 uppercase tracking-widest">Recipient</span>
                                            <span className="font-mono text-[11px] font-bold text-[#0A0B0F] dark:text-gray-300 truncate max-w-[200px] bg-stone-50 dark:bg-white/5 px-2 py-1 rounded-sm">{recipient}</span>
                                        </div>
                                        <div className="flex justify-between p-4 items-center">
                                            <span className="text-[11px] font-bold text-stone-500 uppercase tracking-widest">Gas Estimate</span>
                                            <div className="text-right">
                                                <span className="font-mono text-[11px] font-bold text-[#0A0B0F] dark:text-emerald-500">{gasEstimate}</span>
                                                <div className="text-[8px] text-stone-500 font-black uppercase tracking-tighter">Fast Priority</div>
                                            </div>
                                        </div>
                                        <div className="flex justify-between p-4 items-center">
                                            <span className="text-[11px] font-bold text-stone-500 uppercase tracking-widest">Privacy Tier</span>
                                            <div className="flex items-center gap-2">
                                                {stealthMode && <ShieldCheck size={12} className="text-emerald-500" />}
                                                <span className={`text-[10px] font-black uppercase tracking-widest ${stealthMode ? 'text-emerald-500' : 'text-stone-400'}`}>
                                                    {stealthMode ? 'Stealth Protocol' : 'Standard Transparent'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="p-4 bg-emerald-500/5 rounded-sm border border-emerald-500/10 flex items-start gap-3">
                                    <CheckCircle size={14} className="text-emerald-500 shrink-0 mt-0.5" />
                                    <p className="text-[10px] text-emerald-800 dark:text-stone-400 leading-relaxed font-medium uppercase tracking-tight">
                                        {simResult || "Transaction simulation successful. State parity verified on-chain."}
                                    </p>
                                </div>

                                <div className="pt-4 space-y-3">
                                    <Button onClick={handleSend} variant="secondary" isLoading={isProcessing} className="w-full h-16 rounded-sm text-base font-black uppercase tracking-[0.3em] shadow-2xl bg-[#0A0B0F] dark:bg-white text-white dark:text-black">
                                        Sign & Execute
                                    </Button>
                                    <p className="text-center text-[9px] text-stone-400 font-black uppercase tracking-[0.4em]">Requires Biometric Authorization</p>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                <div className="p-4 bg-gray-50 dark:bg-black/40 text-center border-t border-stone-100 dark:border-white/5">
                    <div className="flex items-center justify-center gap-2 text-[9px] text-stone-400 font-black uppercase tracking-[0.2em] opacity-60">
                        <Lock size={10} className="text-emerald-500" /> SECURED VIA GHOST-OS AES-256-GCM
                    </div>
                </div>
            </motion.div>
        </motion.div>
    );
};
